import {useContext} from 'react';
import {useQuery} from '@tanstack/react-query';
import {TableauView} from '@/helpers/tableau';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAuthStore} from '@/store/auth_store';
import Loading from '@common/Loading';
import {Iframe} from '@/components/pages/analytics/Report.styles';
import PermissionsHelpers from '@/helpers/permissions_helpers';
import {Feature, FeatureNew, FeaturePermissionLevel} from "@/api/dto/permissions";
import {FeatureToggle} from "@/api/dto/dto";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";

type Props = {
  view: TableauView;
}

export default function Report(props: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const { currentUser } = useAuthStore();
  const {checkBooleanToggle} = useFeatureToggles();

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);

  const { data: rosters } = useQuery({
    queryKey: ['rosters'],
    queryFn: () => carecoApi?.getRosters(),
    enabled: !!carecoApi,
  });

  const { data: ticket, isFetching, error } = useQuery({
    queryKey: ['analytics', props.view],
    queryFn: () => carecoApi?.getAnalyticsTicket(),
    enabled: !!carecoApi,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  if (!currentUser || !rosters || isFetching) {
    return <Loading />;
  }

  if (error) {
    return <div>Report could not be loaded</div>;
  }

  const availableRosters = rosters.data.data.filter((roster) =>
    isNewPermissionsEnabled ?
      PermissionsHelpers.canForUserFeaturePermissionRosterNew(
        currentUser,
        roster,
        FeaturePermissionLevel.VIEW,
        FeatureNew.ANALYTICS
      ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
        currentUser,
        roster,
        FeaturePermissionLevel.VIEW,
        Feature.USER_REPORTING
      ));

  const src = `${process.env.REACT_APP_TABLEAU_URL}/trusted/${ticket}` +
    `/t/${process.env.REACT_APP_TABLEAU_SITE}` +
    `/views/${props.view}` +
    `?:embed=yes` +
    `&group_id=${availableRosters.map((roster) => roster.id).filter(Boolean).join(',')}` +
    `&group_uuid=${availableRosters.map((roster) => roster.uuid).filter(Boolean).join(',')}`;

  return <Iframe src={src} title="Report" />;
}
